<script setup lang="ts">
import {computed, getCurrentInstance, nextTick, onMounted, PropType, ref} from 'vue';
import {CascaderOption, ElDialog, ElMessage} from 'element-plus';
import {CabOrganization, CabTheme, CabUser, OrgThemeCombi, Role, type SessieTelling, toCab, uuidOf} from 'common';
import {
    createObject,
    deleteObject,
    getCurrentUser,
    listObjects,
    readObject,
    sessieOverzicht,
    syncUsers,
    updateObject,
} from '@/base/callBackend';
import UserStrip from '@/views/admin/UserStrip.vue';
import AuthLogPanel from '@/views/admin/AuthLogPanel.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import SyncButton from '@/views/icons/SyncIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';
import InfoIcon from '@/views/icons/InfoIcon.vue';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {ROLE_DESCRIPTIONS, USER_CHANGE_EMIT, USER_REMOVE_EMIT, USER_SESSIONS_ENDED_EMIT} from '@/base/cabConstants';
import {roleLabel} from '@/base/utils';

const users           = ref<CabUser[]>([]);
const organizations   = ref<CabOrganization[]>([]);
const filterActive    = ref<boolean>(false);
const filteredUsers   = ref<CabUser[]>([]);
const orgThemeOptions = ref<CascaderOption[]>([]);
const me              = ref<CabUser>();
const roleInfoVisible = ref<boolean>(false);
const tellingen       = ref<Record<string, SessieTelling>>({});

const props = defineProps({
    selectableRoles: {
        type    : Object as PropType<Role[]>,
        required: false,
    },
});

const infoRoles = computed<Role[]>(() => props.selectableRoles ?? Object.values(Role));
const isAdmin   = computed<boolean>(() => me.value?.roles.includes(Role.admin) ?? false);

onMounted(async () => {
    me.value              = toCab(await getCurrentUser());
    users.value           = await listObjects(CabUser);
    users.value           = users.value.filter(user => !user.email || ![me.value!.email!, 'admin@openwalnoot.com'].includes(user.email)); //Never modify yourself or main admin
    organizations.value   = await listObjects(CabOrganization);
    orgThemeOptions.value = await makeCascaderOptions();
    await loadTellingen();
});

async function loadTellingen() {
    tellingen.value = await sessieOverzicht();
}

//=====================================================================================
async function onUserRemove(user: CabUser) {
    await deleteObject(user);
    users.value = users.value.filter((u) => uuidOf(u) !== uuidOf(user));
}

async function onUserAdd() {
    filterActive.value = false;
    const newUser      = new CabUser();
    if (props.selectableRoles) {
        newUser.roles = [props.selectableRoles[0]];
    }
    newUser.organizations = me.value!.organizations;
    newUser.themes        = me.value!.themes;
    if (!await createObject(newUser)) {
        return; // createObject already showed why it failed
    }
    users.value = [newUser, ... users.value];
    await nextTick();
    getUserStrip(makeStripRef(newUser))?.firstEdit();
}

async function onUserChange(user: CabUser) {
    await updateObject(user);
    users.value = users.value.map((u) =>
        uuidOf(u) === uuidOf(user) ? user : u,
    );

}

const instance = getCurrentInstance();

function getUserStrip(ref: string): InstanceType<typeof UserStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabUser) {
    return `UserStrip-${uuidOf(x)}`;
}

async function onUserSync() {
    filterActive.value = false;
    let result;
    try {
        result = await syncUsers();
    } catch (error) {
        ElMessage.error('Het syncen van de rapporteurs met de inlog administratie is mislukt');
        return;
    }
    await loadTellingen();

    if (result?.errors?.length) {
        result.errors.forEach((message: string) => ElMessage.error({
            message,
            showClose: true,
            duration : 0,
        }));
    } else {
        ElMessage({
            message: 'Rapporteurs zijn gesynced met de inlog administratie',
            type   : 'success',
        });
    }
}

async function makeCascaderOptions(): Promise<CascaderOption[]> {
    if (me.value!.roles.includes(Role.admin)) {
        return organizations.value.map((o) => ({
            label   : o.name,
            value   : uuidOf(o),
            children: o.themes.map((t) => ({
                label: t.name,
                value: uuidOf(t),
            })),
        }));
    } else {
        // Get allowed org/theme pairs for this user
        const allowedCombis: OrgThemeCombi[] = me.value!.getOrgThemeCombis();

        // Group allowed themes by organization UUID
        const orgMap: Record<string, { org: CabOrganization; themes: CabTheme[] }> = {};
        for (let {
            organization,
            theme
        } of allowedCombis) {
            organization          = await readObject(organization);
            theme                 = await readObject(theme);
            const orgUuid: string = uuidOf(organization);
            if (!orgMap[orgUuid]) {
                orgMap[orgUuid] = {
                    org   : organization,
                    themes: [],
                };
            }
            orgMap[orgUuid].themes.push(theme);
        }

        // Build cascader options
        return Object.values(orgMap).map(({
            org,
            themes,
        }) => ({
            label   : org.name,
            value   : uuidOf(org),
            children: themes.map((t) => ({
                label: t.name,
                value: uuidOf(t),
            })),
        }));
    }
}

//=====================================================================================
</script>

<template>
    <div class="flex flex-col flex-1 mx-28 my-5">
        <div class="flex flex-row flex-1 mb-1">
            <PlusButton @click="onUserAdd()"/>
            <SyncButton @click="onUserSync()"/>
            <div class="text-2xl ml-2">
                Gebruikers
            </div>
            <InfoIcon class="ml-4 mt-1 cursor-pointer"
                      @click="roleInfoVisible = true"/>
            <div class="flex-1"></div>
            <GeneralFilter v-model:active="filterActive"
                           v-model:cabList="users"
                           v-model:cabListFiltered="filteredUsers"
                           :id="'UserList'"/>
        </div>
        <div class="flex flex-row items-center w-full text-xs text-gray-500 mx-0.5 px-1"
             v-if="filteredUsers.length"
        >
            <div class="w-60 px-3">naam</div>
            <div class="w-60 px-3">email</div>
            <div class="w-40 px-3">rol</div>
            <div class="flex-1 px-3">instelling / thema</div>
            <div class="w-12 text-center">2FA</div>
            <div class="w-16 text-center">sessies</div>
            <div class="icon-button-space"/>
            <div class="icon-button-space"/>
        </div>
        <UserStrip
            v-for="user in filteredUsers"
            :key="uuidOf(user)"
            :user="user"
            :allOrganizations="organizations"
            :selectableRoles="props.selectableRoles"
            :orgThemeOptions="orgThemeOptions"
            :me="me!"
            :allUsers="users"
            :telling="tellingen[user.email?.toLowerCase() ?? '']"
            :ref="makeStripRef(user)"
            v-on:[USER_REMOVE_EMIT]="onUserRemove"
            v-on:[USER_CHANGE_EMIT]="onUserChange"
            v-on:[USER_SESSIONS_ENDED_EMIT]="loadTellingen"
        />
        <AuthLogPanel v-if="isAdmin"/>
    </div>

    <el-dialog
        v-model="roleInfoVisible"
        :show-close="false"
        :close-on-press-escape="true"
        :close-on-click-modal="true"
    >
        <template #header>
            <div class="text-xl text-center w-full bg-gray-700 text-gray-200 py-2">
                Rollen en toegang
            </div>
        </template>
        <div class="flex flex-col min-w-60 bg-gray-300 p-3">
            <table class="table-auto border-collapse border w-full bg-white">
                <tbody>
                <tr v-for="role in infoRoles" :key="role">
                    <td class="border p-2 align-top whitespace-nowrap font-semibold">
                        {{ roleLabel(role) }}
                    </td>
                    <td class="border p-2 align-top">
                        {{ ROLE_DESCRIPTIONS[role] }}
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </el-dialog>
</template>
